/**
 * O depósito do `resultados` da simulação criada pela conversa (spec-007).
 *
 * POR QUE ELE EXISTE
 * ------------------
 * O commit da simulação exige `resultados` — as 25 chaves que `simulate(S)`
 * produz —, e ele é calculado **aqui**, no navegador, porque o motor é um só
 * (spec-007 §1.1). Já a confirmação viaja como **texto**: o servidor reconhece
 * a frase do consultor antes de o modelo vê-la, e nenhuma frase carrega um
 * objeto de 25 chaves.
 *
 * Daí o depósito: o cartão guarda o `resultados` sob o `preview_id`, e a frase
 * de protocolo volta a bastar. O contrato inteiro está em
 * `.specify/.../spec-007-simulacao-pelo-chat/contracts/deposito-de-resultados.md`.
 *
 * ★ A ORDEM É PARTE DO CONTRATO: **depositar → só com `status === 200`, mandar a
 * frase**. Frase sem depósito faz o servidor recusar por um motivo que o
 * consultor não tem como entender — ele veria "não foi possível salvar" sobre
 * uma tela que mostrava os números.
 *
 * ★ O que NÃO vai no corpo: `id_escritorio` e `id_profissional`. Eles vêm da
 * sessão, no servidor. Mandá-los daqui seria oferecer ao cliente o eixo que
 * decide de quem é a escrita.
 */
import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { feeFixo as endpoint } from '@modules/fee-fixo/constants/URL'

/**
 * Falha de rede — o depósito não aconteceu, e o cartão não pode mandar a frase.
 *
 * A mensagem fala do EFEITO ("não confirme"), não da causa técnica: quem lê é
 * o consultor no meio de uma conversa, e a ação dele é a mesma para timeout,
 * 502 ou queda de rede.
 */
export const MSG_ERRO_DEPOSITO =
  'Não consegui guardar os números desta simulação. Não confirme ainda — ' +
  'rode a conta de novo e tente outra vez.'

/** 200 sem corpo é resposta quebrada; tratá-la como sucesso mandaria a frase. */
export const MSG_RESPOSTA_SEM_STATUS =
  'A resposta do servidor veio incompleta ao guardar os números. Não ' +
  'confirme ainda — rode a conta de novo.'

/** Sem `preview_id` o servidor responde 404: recusa local, sem gastar a rede. */
export const MSG_DEPOSITO_SEM_PREVIEW =
  'Esta simulação não tem registro no servidor. Rode a conta de novo antes ' +
  'de salvar.'

class ApiFeeFixoSimulacaoDeposito extends APITemplate {
  /**
   * Guarda o `resultados` sob o `preview_id`, para o commit lê-lo.
   *
   * ⚠️ **Não normaliza status não-200 para sucesso.** Os três que o servidor
   * devolve são acionáveis e a mensagem dele já diz o que fazer — 404 pede
   * rodar a simulação de novo (o preview expirou, foi substituído por um ajuste
   * ou não é seu); 400 acusa números diferentes para o mesmo id; 500 é o
   * fail-closed do registro. Reescrever qualquer um deles aqui trocaria uma
   * instrução certa por uma genérica.
   *
   * @param {Object} params
   * @param {string} params.previewId  O id que o preview emitiu para ESTE cenário.
   * @param {Object} params.resultados A saída de `simulate(S)`, como o cartão a
   *                                   calculou. Vai no corpo **serializado**
   *                                   (ver o comentário no método).
   * @returns {Promise<{status: number, message: string, value: *}>}
   */
  async depositar({ previewId, resultados } = {}) {
    // Guard ANTES da URL, como as outras classes do módulo: `preview_id:
    // "undefined"` no FormData só faz round-trip para um 404.
    if (!previewId) {
      return this.buildErrorResponse({
        status: 400,
        message: MSG_DEPOSITO_SEM_PREVIEW,
        value: null,
      })
    }
    const r = await con.post(endpoint.simulacaoDeposito, {
      preview_id: previewId,
      // ★ `JSON.stringify` NÃO é preciosismo — sem ele o depósito nunca
      // funciona no navegador (review de arquitetura, 24/08/2026).
      //
      // `con.post` sem `{json: true}` serializa TUDO em FormData, e o
      // `montarFormData` (`services/connection.js`) só faz `JSON.stringify`
      // quando `value.length !== undefined` — isto é, para ARRAYS. Um objeto
      // puro cai no `else` e vira `formData.append(key, value)`, que o
      // FormData coage para a string literal `"[object Object]"`. Do outro
      // lado, `_json_dict_parser` levanta `ValueError` nela, e o reqparse
      // aborta em **400 mesmo com `required=False`** — `required` cobre
      // AUSÊNCIA, não falha de tipo. Resultado: o card mostra o erro, a frase
      // nunca é mandada, e o cenário nunca é salvo pela conversa.
      //
      // ⚠️ Nenhum unit via isso porque **cada lado dublava o outro**: aqui o
      // `con` é `jest.mock`ado e a asserção lia o objeto ANTES da
      // serialização; lá o teste do Resource trocava o parser inteiro por um
      // MagicMock. É o "dois lados do mesmo valor, nunca comparados" — e a
      // rede que fecha isso é `apiFeeFixoSimulacaoDeposito.corpo.unit.js`,
      // que roda o `montarFormData` REAL.
      //
      // É também o que as outras DUAS gravações de análise do módulo já
      // faziam (`modal-salvar-analise.vue:221`,
      // `view-fee-fixo-simulacao-logic.js:638`). Eu não olhei o par antes de
      // escrever esta classe; a review olhou.
      resultados: JSON.stringify(resultados || {}),
    })

    if (!r) {
      return this.buildErrorResponse({
        message: MSG_ERRO_DEPOSITO,
        value: null,
      })
    }
    if (typeof r.status !== 'number') {
      return this.buildErrorResponse({
        message: MSG_RESPOSTA_SEM_STATUS,
        value: null,
      })
    }
    return r
  }
}

const apiFeeFixoSimulacaoDeposito = new ApiFeeFixoSimulacaoDeposito()

export { apiFeeFixoSimulacaoDeposito }
